import { useContext, useEffect, MouseEvent } from 'react'
import { styled, Theme, SxProps } from '@mui/material/styles'
import MenuList from '@mui/material/MenuList'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Chip from '@mui/material/Chip'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import GlobalContext from '@/context/GlobalContext'
import { useSizes } from '@/hooks/useSizes'
import { useNavigate, useLocation } from 'react-router-dom'
import { labelToPath } from '@/router/RoutesUtils'

import Typography from '@mui/material/Typography'
import { MenuItemType } from './types'

const MenuContainer = styled(MenuList)(() => ({
  overflow: 'auto',
  '&::-webkit-scrollbar': { display: 'none' },
  padding: 0,
}))

const MenuListItem = styled(MenuItem, {
  shouldForwardProp: prop => prop !== 'isCollapsed',
})<{ isCollapsed?: boolean }>(({ theme, isCollapsed }) => ({
  display: 'flex',
  gap: isCollapsed ? '0' : '12px',
  color: theme.palette.text.secondary,
  padding: `${theme.spacing(2.25)} ${theme.spacing(2)}`,
  borderRadius: '4px',
  justifyContent: isCollapsed ? 'center' : 'flex-start',
  transition: theme.transitions.create(['gap', 'padding', 'justify-content'], {
    duration: theme.transitions.duration.standard,
    easing: theme.transitions.easing.easeInOut,
  }),
  '& svg': {
    flexShrink: 0,
    width: '20px',
    height: '20px',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.secondary.lighter,
    color: theme.palette.primary.main,
  },
  '&.Mui-selected:hover': {
    backgroundColor: theme.palette.secondary.lighter,
    color: theme.palette.primary.main,
  },
}))

export interface MenuProps {
  customOnClickItemOnly?: boolean
  onClickItem?: (
    item: string,
    event:
      | MouseEvent<HTMLLIElement, MouseEvent>
      | MouseEvent<HTMLDivElement, MouseEvent>
      | MouseEvent
  ) => void
  menuItems: MenuItemType[]
  sx?: SxProps<Theme>
  useLinks?: string
  testid?: string
  isCollapsed?: boolean
}
const Menu = ({
  customOnClickItemOnly,
  onClickItem,
  menuItems,
  sx = {},
  useLinks = '',
  testid,
  isCollapsed = false,
}: MenuProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const globalContext = useContext(GlobalContext)
  const itemSelected = globalContext?.selectedTab

  useEffect(() => {
    if (useLinks && location.pathname.startsWith(useLinks)) {
      const pathSegments = location.pathname.split('/')
      const currentPathSegment = pathSegments[pathSegments.length - 1]

      const matchingItem = menuItems
        .flatMap(section => section.items)
        .find(item => labelToPath(item.label) === currentPathSegment)

      if (matchingItem) {
        globalContext?.setSelectedTab(matchingItem.label)
      } else if (menuItems?.[0]?.items?.[0]?.label) {
        globalContext?.setSelectedTab(menuItems[0].items[0].label)
      }
    } else if (menuItems?.[0]?.items?.[0]?.label && !itemSelected) {
      globalContext?.setSelectedTab(menuItems[0].items[0].label)
    }
  }, [location.pathname, menuItems, useLinks, globalContext, itemSelected])

  const { isGreaterMd: isLargeScreen } = useSizes()

  const onClick = (
    item: string,
    event:
      | MouseEvent<HTMLLIElement, MouseEvent>
      | MouseEvent<HTMLDivElement, MouseEvent>
      | MouseEvent
  ) => {
    if (onClickItem && customOnClickItemOnly) {
      return onClickItem(item, event)
    }
    if (useLinks !== '') {
      navigate(`${useLinks}/${labelToPath(item)}`)
    }

    globalContext?.setSelectedTab(item)
    if (onClickItem) onClickItem(item, event)

    return null
  }

  return isLargeScreen ? (
    <MenuContainer sx={sx} data-testid={testid}>
      {menuItems.map(section => (
        <ul
          key={`${section.sectionTitle}`}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {section.items
            .filter(item => !(isCollapsed && item.disabled))
            .map(item => {
              const menuItem = (
                <MenuListItem
                  onClick={event =>
                    !item.disabled && onClick(item.label, event)
                  }
                  selected={itemSelected === item.label && !item.disabled}
                  data-label={item.label}
                  data-testid={item.testid}
                  key={`${item}-${item.label}`}
                  disabled={item.disabled}
                  isCollapsed={isCollapsed}
                  sx={
                    item.disabled
                      ? {
                          opacity: 0.6,
                          cursor: 'default',
                          pointerEvents: 'none',
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        }
                      : undefined
                  }
                >
                  {item?.icon}
                  {!isCollapsed && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        opacity: 1,
                        transition: theme =>
                          theme.transitions.create('opacity', {
                            duration: theme.transitions.duration.standard,
                          }),
                      }}
                    >
                      <Typography variant="subtitle2">{item.label}</Typography>
                      {item.badgeContent !== undefined &&
                        item.badgeContent > 0 && (
                          <Typography variant="caption">
                            {item.badgeContent}
                          </Typography>
                        )}
                    </Box>
                  )}
                </MenuListItem>
              )

              return isCollapsed && !item.disabled ? (
                <Tooltip
                  title={item.label}
                  placement="right"
                  key={`${item}-${item.label}`}
                >
                  {menuItem}
                </Tooltip>
              ) : (
                menuItem
              )
            })}
        </ul>
      ))}
    </MenuContainer>
  ) : (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        padding: theme => theme.spacing(3.5, 1),
      }}
      data-testid={testid}
    >
      {menuItems.map((section, iSections) =>
        section.items
          .filter(item => !item.disabled)
          .map((item, i) => (
            <Chip
              variant="outlined"
              key={`chip-${item.label}`}
              label={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography component="span">{item.label}</Typography>
                  {item.badgeContent !== undefined && item.badgeContent > 0 && (
                    <Badge
                      badgeContent={item.badgeContent}
                      color="secondary"
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'relative',
                          transform: 'none',
                          borderRadius: '100%',
                        },
                      }}
                    />
                  )}
                </Box>
              }
              data-label={item.label}
              onClick={event => onClick(item.label, event)}
              sx={theme => ({
                ...(i === 0 && {
                  marginLeft: 2,
                }),
                ...(iSections === menuItems.length - 1 &&
                  i === section.items.length - 1 && {
                    marginRight: `${theme.spacing(2)} !important`,
                  }),
                ...(itemSelected === item.label && {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&.MuiChip-clickable:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                }),
              })}
            />
          ))
      )}
    </Stack>
  )
}

export default Menu
